import { computed, type Ref } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { LaunchType } from '@/modules/common/enums/launch-type.ts';
import { ControlType } from '@/modules/common/enums/control-type.ts';
import { DeadlineType } from '@/modules/common/enums/deadline-type.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import type { Deadline } from '@/modules/common/types/ui.ts';
import { DeadlineApi } from '@/modules/core/api/deadline.api.ts';
import type { RadioOption } from '@/modules/ui/components/ui/UiRadio.vue';
import { createSchema } from '@/modules/ui/models/schema/schema.ts';
import { SchemaCombobox } from '@/modules/ui/models/schema/schema-combobox.ts';
import { SchemaDeadline } from '@/modules/ui/models/schema/schema-deadline.ts';
import { SchemaCheckbox } from '@/modules/ui/models/schema/schema-checkbox.ts';
import { SchemaMessage } from '@/modules/ui/models/schema/schema-message.ts';
import { useDocumentAccess } from '@/modules/document/composables/use-document-access.ts';
import type { StepLaunchFormData } from '@/modules/document/document-master/types/launch-form.ts';

export const LAUNCH_TYPE_OPTIONS: RadioOption<LaunchType>[] = [
  {
    code: LaunchType.Now,
    label: 'document.ui.radio.option.launchTypeNow',
    color: 'green',
  },
  {
    code: LaunchType.Delayed,
    label: 'document.ui.radio.option.launchTypeDelayed',
  },
  {
    code: LaunchType.None,
    label: 'document.ui.radio.option.launchTypeNone',
    color: 'red',
  },
];

export const CONTROL_TYPE_IMMEDIATELY: Option<ControlType> = {
  label: 'document.ui.combobox.option.controlTypeImmediately',
  code: ControlType.Immediately,
};

export const CONTROL_TYPE_AFTER: Option<ControlType> = {
  label: 'document.ui.combobox.option.controlTypeAfter',
  code: ControlType.After,
};

export const CONTROL_TYPE_BEFORE: Option<ControlType> = {
  label: 'document.ui.combobox.option.controlTypeBefore',
  code: ControlType.Before,
};

export function createLaunchDeadline(): Deadline {
  return {
    type: DeadlineType.WorkingDays,
    days: 1,
    date: DateTime.now().plus({ days: 1 }),
  };
}

export function createLaunchFormData(): StepLaunchFormData {
  return {
    controlType: ControlType.Immediately,
    nowDeadline: createLaunchDeadline(),
    delayedDeadline: createLaunchDeadline(),
    stopControlBeforeLaunch: false,
  };
}

export function getControlTypeOptions(beforeDate: DateTime | undefined) {
  return [CONTROL_TYPE_IMMEDIATELY, beforeDate && CONTROL_TYPE_BEFORE, CONTROL_TYPE_AFTER].filter(
    Boolean
  ) as Option<ControlType>[];
}

export function createControlTypeSchema(options: Option<ControlType>[], name = 'controlType') {
  return new SchemaCombobox({
    name,
    options,
    required: true,
    expanded: true,
  });
}

export function createControlDeadlineSchema(
  name: string,
  controlType: ControlType,
  beforeDate: DateTime | undefined,
  withControlTypePrefix = false
) {
  const isBefore = controlType === ControlType.Before;
  const showControls = controlType !== ControlType.Immediately;

  return new SchemaDeadline({
    getDeadline: DeadlineApi.getDeadline,
    getWorkDaysDiff: DeadlineApi.getWorkDaysDiff,
    name,
    required: showControls,
    validate: showControls,
    controls: showControls,
    direction: isBefore ? 'backward' : 'forward',
    from: isBefore ? beforeDate : DateTime.now(),
    slot: withControlTypePrefix ? { from: 'control-type', to: 'prefix' } : undefined,
  });
}

export function createDelayedLaunchSchema(editLossWarning = false) {
  return createSchema(
    new SchemaDeadline({
      getDeadline: DeadlineApi.getDeadline,
      getWorkDaysDiff: DeadlineApi.getWorkDaysDiff,
      name: 'delayedDeadline',
      required: true,
    }),
    new SchemaCheckbox({
      name: 'stopControlBeforeLaunch',
      slot: 'stop-control-before-launch',
    }),
    editLossWarning &&
      new SchemaMessage({
        name: 'editLossWarning',
        type: 'warning',
        slot: 'edit-loss-warning',
      }),
    new SchemaMessage({
      name: 'launchTypeDelayedMessage',
      type: 'info',
      slot: 'launch-type-delayed-message',
    })
  );
}

export function useLaunchMessages() {
  const { t } = useI18n();

  function formatDays({ days, type }: Deadline) {
    return t(type === DeadlineType.WorkingDays ? 'ui.deadline.workingDays' : 'ui.deadline.calendarDays', {
      count: days,
    });
  }

  function formatDate({ date }: Deadline) {
    // TODO: Используйте use-formatted-date
    return date.toLocaleString(DateTime.DATE_FULL);
  }

  function formatControlMessage(start: string, controlType: ControlType, deadline: Deadline) {
    const end =
      controlType === ControlType.Before
        ? 'ui.message.documentWillBeLaunched.before'
        : 'document.ui.message.documentWillBeLaunched.after';

    return deadline.type === DeadlineType.Date
      ? `${start} ${formatDate(deadline)}`
      : `${start} ${formatDate(deadline)}, ${t(end, { days: formatDays(deadline) })}`;
  }

  function formatDelayedMessage(stopControlBeforeLaunch: boolean, deadline: Deadline) {
    const action = !stopControlBeforeLaunch
      ? 'ui.message.documentWillBeControlDelayed.immediatelyLaunched'
      : 'ui.message.documentWillBeControlDelayed.delayedLaunched';

    const start = `${t('document.ui.message.documentWillBeControlDelayed.start')} ${t(action)}`;
    const end = t('document.ui.message.documentWillBeLaunched.after', { days: formatDays(deadline) });

    return deadline.type === DeadlineType.Date
      ? `${start} ${formatDate(deadline)}`
      : `${start} ${formatDate(deadline)}, ${end}`;
  }

  return { t, formatControlMessage, formatDelayedMessage };
}

export function useLaunchOptions(formData: Ref<StepLaunchFormData>, getBeforeDate: () => DateTime | undefined) {
  const { t, formatControlMessage, formatDelayedMessage } = useLaunchMessages();
  const { isAdmin } = useDocumentAccess(null);

  const nowMessage = computed(() => {
    const { controlType, nowDeadline } = formData.value;

    return controlType === ControlType.Immediately
      ? t('document.ui.message.documentWillBeLaunchedImmediately')
      : formatControlMessage(t('document.ui.message.documentWillBeLaunched.start'), controlType, nowDeadline);
  });

  const delayedMessage = computed(() =>
    formatDelayedMessage(formData.value.stopControlBeforeLaunch, formData.value.delayedDeadline)
  );

  const schemas = computed(() => {
    const beforeDate = getBeforeDate();
    const controlType = formData.value.controlType;

    return {
      [LaunchType.Now]: createSchema(
        createControlDeadlineSchema('nowDeadline', controlType, beforeDate, true),
        new SchemaMessage({
          name: 'launchTypeNowMessage',
          type: 'info',
          slot: 'launch-type-now-message',
        })
      ),
      [LaunchType.Delayed]: createDelayedLaunchSchema(formData.value.stopControlBeforeLaunch && !isAdmin.value),
      [LaunchType.None]: createSchema(
        new SchemaMessage({
          name: 'launchTypeNoneMessage',
          type: 'warning',
          slot: 'launch-type-none-message',
        })
      ),
    };
  });

  return { schemas, nowMessage, delayedMessage };
}
